import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { MAX_FREE_TRIAL_COUNT } from '@/constants/tools/ai-video-generator'
import { aiVideoGeneratorApi } from '@/redux/api/tools/aiVideoGeneratorApi'
import { TOOL } from '@/constants/tools/tool'
import { useAppDispatch } from '@/hooks/redux'
import { projectActions } from '@/redux/stores/projectStore'
import { MODAL_NAME } from '@/components/editor/stripe/constants'
import { StripeModal } from '@/components/editor/stripe/StripeModal'
import {
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
} from '@/constants/terms-and-policy-url'

import { ToolsLayout } from '../../common/components/ToolsLayout'
import { useToolsNotification } from '../../common/hooks/notification'
import { useChargeUsage } from '../../common/hooks/charge-usage'
import { CannotChargeFreeTrialModal } from '../../common/components/CannotChargeFreeTrialModal'

import { useGenerateVideo } from '../common/hooks/generate'

import { FullLoadingProgress } from './FullLoadingProgress'
import { GeneratorForm } from './GeneratorForm'

type AiVideoGeneratorProps = {
  topic?: string

  redirected?: {
    input: {
      topic: string
    }
    video: {
      videoUrl: string
      thumbnailUrl: string
      width: number
      height: number
      /**
       * unit: seconds
       */
      duration: number
    }
    error: string
  }
}

export const AiVideoGenerator: FC<AiVideoGeneratorProps> = ({
  topic: topicFromProps = '',
  redirected,
}) => {
  const dispatch = useAppDispatch()
  const router = useRouter()

  const { t } = useTranslation('common')

  const { open: openNotification, contextHolder } = useToolsNotification()

  /**
   * 생성 중 오류 발생 시 알림
   */
  const openGeneratingErrorNotification = useCallback(() => {
    openNotification(
      t(
        'An error while generating your video. Please check the topic and try again later.',
      ),
    )
  }, [openNotification, t])

  /**
   * 프로젝트 생성 중 오류가 발생하여 현재 페이지로 리디렉트 된 경우 알림을 바로 표시한다.
   */
  useEffect(() => {
    if (!redirected) {
      return
    }

    openGeneratingErrorNotification()
  }, [redirected, openGeneratingErrorNotification])

  /**
   * 플랜 결제 모달 열기
   */
  const openSubscriptionListModal = useCallback(() => {
    dispatch(projectActions.setOpenedModal(MODAL_NAME.SUBSCRIPTION_LIST))
  }, [dispatch])

  /**
   * 무료 체험 횟수를 모두 소진한 경우 표시하는 모달
   */
  const [
    isOpenedCannotChargeFreeTrialModal,
    setIsOpenedCannotChargeFreeTrialModal,
  ] = useState(false)

  const openCannotChargeFreeTrialModal = useCallback(() => {
    setIsOpenedCannotChargeFreeTrialModal(true)
  }, [])

  /**
   * 무료 체험 횟수 소진 모달을 닫을 경우 로그인 페이지로 이동
   */
  const handleCloseCannotChargeFreeTrialModal = useCallback(() => {
    return router.push({
      pathname: '/auth/signin',
      query: {
        redirect: router.asPath,
      },
    })
  }, [router])

  /**
   * 로그인 여부, 남은 크레딧 정보, 무료 체험 횟수 등...
   */
  const {
    isLoadingUserInfo,
    isLoggedIn,
    isPaidUser,
    remainingFreeTrialCount,
    checkCanCharge,
    chargeUsage,
  } = useChargeUsage({
    tool: TOOL.AI_VIDEO_GENERATOR,
    maxFreeTrialCount: MAX_FREE_TRIAL_COUNT,
    /**
     * 크레딧 차감이 불가능한 경우 플랜 결제 모달 열기
     */
    onCannotChargeCredit: openSubscriptionListModal,
    /**
     * 무료 체험 횟수를 모두 사용한 경우 안내 모달 표시 후 로그인 페이지로 이동
     */
    onCannotChargeFreeTrial: openCannotChargeFreeTrialModal,
  })

  /**
   * 로컬 스테이트...
   */
  const [topic, setTopic] = useState(() => {
    /**
     * 프로젝트 생성 중 오류가 발생하여 현재 페이지로 리디렉트 된 경우\
     * 오류가 발생한 건의 토픽을 우선 사용한다.
     */
    if (!!redirected) {
      return redirected.input.topic
    }

    return topicFromProps
  })

  /**
   * API 에러가 발생한 상태에서 파일이 변경되면 에러 상태를 해제한다.
   */
  const [isChangedAfterError, setIsChangedAfterError] = useState(false)

  const trimmedTopic = useMemo(() => topic.trim(), [topic])

  const {
    generate: generateVideo,
    generated: generatedVideo,
    error: errorGenerateVideo,
    isGenerated: isGeneratedVideo,
    isGenerating: isGeneratingVideo,
  } = useGenerateVideo({
    topic: trimmedTopic,
    initialGenerated: !!redirected
      ? {
          input: redirected.input,
          video: redirected.video,
        }
      : undefined,
  })

  const [
    mutateCreateJob,
    { isLoading: isMutatingCreateJob, error: errorCreateJob },
  ] = aiVideoGeneratorApi.useCreateJobMutation()

  /**
   * 토픽이 변경되면 에러 상태 초기화
   */
  useEffect(() => {
    setIsChangedAfterError(true)
  }, [topic])

  /**
   * API 에러가 갱신되면 토픽이 변경되기 전까지는 에러 상태로 표시한다.
   */
  useEffect(() => {
    setIsChangedAfterError(false)
  }, [errorGenerateVideo, errorCreateJob])

  /**
   * 리스폰스로 에러가 넘어온 경우 에러 토스트를 표시한다.
   */
  useEffect(() => {
    if (!errorGenerateVideo && !errorCreateJob) {
      return
    }

    openGeneratingErrorNotification()
  }, [openGeneratingErrorNotification, errorGenerateVideo, errorCreateJob])

  /**
   * disabled 상태, 이벤트 핸들러 등...
   */
  const disabledChangeTopic = isGeneratingVideo || isMutatingCreateJob

  const disabledGenerate =
    !trimmedTopic || isGeneratingVideo || isMutatingCreateJob

  const handleGenerate = useCallback(async () => {
    if (isGeneratingVideo) {
      return
    }

    if (isMutatingCreateJob) {
      return
    }

    if (!trimmedTopic) {
      return
    }

    /**
     * 사용량 차감이 가능한지 검사
     */
    if (!checkCanCharge()) {
      return
    }

    const generated = await generateVideo()

    if (!generated) {
      return
    }

    /**
     * 사용량 차감\
     * 무료 사용 횟수 또는 크레딧 차감
     */
    await chargeUsage(generated.video.duration)
  }, [
    isGeneratingVideo,
    isMutatingCreateJob,
    trimmedTopic,
    checkCanCharge,
    generateVideo,
    chargeUsage,
  ])

  const disabledExport =
    isGeneratingVideo || isMutatingCreateJob || !isGeneratedVideo

  const handleDownload = useCallback(async () => {
    if (isMutatingCreateJob) {
      return
    }

    if (isGeneratingVideo) {
      return
    }

    if (!generatedVideo) {
      return
    }

    /**
     * 로그인 한 경우 그자리에서 바로 다운로드
     */
    if (isLoggedIn) {
      const el = document.createElement('a')
      el.href = generatedVideo.video.videoUrl
      el.download = 'Deepbrain_ai_video.mp4'
      el.click()
      el.remove()
    } else {
      /**
       * 로그인 하지 않은 경우 대시보드로 이동 후 다운로드
       */
      const { jobId } = await mutateCreateJob({
        topic: generatedVideo.input.topic,
        generatedVideo: generatedVideo.video,
      }).unwrap()

      /**
       * router를 사용하지 않고 location.href를 사용하는 이유
       *
       * 콜백 페이지로 이동 중 오류 발생 시 현재 페이지로 되돌아 오도록 되어 있음\
       * 이 경우 현재 컴포넌트(페이지)가 언마운트/마운트 되지 않고 프로퍼티만 변경됨\
       * 현재 기능 구현 특성 상 초기 데이터를 받아와 처리해야 하는 부분들이 있음\
       * 초기 데이터를 받아오도록 할 때 최초 한 번만 실행하도록 구현했음\
       * 그렇기 때문에 에러 후 언마운트/마운트 되지 않으면 초기 데이터 관련 동작을 하지 않음\
       * 대응 방법은 초기 데이터를 받아오는 부분을 중간에 초기 데이터가 바뀌더라도\
       * 동작하도록 구현을 변경하는 방법도 있지만\
       * 이번에는 간단하게 location.href를 사용하여 강제로 언마운트/마운트 되도록 하고\
       * 기존 구현은 그대로 유지하는 방향으로 함
       */
      window.location.href = `/tools/ai-video-generator/callback/direct-download?jobId=${jobId}`
    }
  }, [
    isMutatingCreateJob,
    isGeneratingVideo,
    generatedVideo,
    mutateCreateJob,
    isLoggedIn,
  ])

  const handleCreateVideo = useCallback(async () => {
    if (isMutatingCreateJob) {
      return
    }

    if (isGeneratingVideo) {
      return
    }

    if (!generatedVideo) {
      return
    }

    const { jobId } = await mutateCreateJob({
      topic: generatedVideo.input.topic,
      generatedVideo: generatedVideo.video,
    }).unwrap()

    /**
     * router를 사용하지 않고 location.href를 사용하는 이유
     *
     * 콜백 페이지로 이동 중 오류 발생 시 현재 페이지로 되돌아 오도록 되어 있음\
     * 이 경우 현재 컴포넌트(페이지)가 언마운트/마운트 되지 않고 프로퍼티만 변경됨\
     * 현재 기능 구현 특성 상 초기 데이터를 받아와 처리해야 하는 부분들이 있음\
     * 초기 데이터를 받아오도록 할 때 최초 한 번만 실행하도록 구현했음\
     * 그렇기 때문에 에러 후 언마운트/마운트 되지 않으면 초기 데이터 관련 동작을 하지 않음\
     * 대응 방법은 초기 데이터를 받아오는 부분을 중간에 초기 데이터가 바뀌더라도\
     * 동작하도록 구현을 변경하는 방법도 있지만\
     * 이번에는 간단하게 location.href를 사용하여 강제로 언마운트/마운트 되도록 하고\
     * 기존 구현은 그대로 유지하는 방향으로 함
     */
    window.location.href = `/tools/ai-video-generator/callback/create-project?jobId=${jobId}`
  }, [isMutatingCreateJob, isGeneratingVideo, generatedVideo, mutateCreateJob])

  /**
   * 전체 로딩 프로그레스 표시 여부\
   * 토픽을 받아온 상태에서의 첫 생성일 때는 전체 로딩 화면을 표시한다.
   */
  const [isShownFullLoadingProgress, setIsShownFullLoadingProgress] =
    useState(false)

  /**
   * 토픽을 받아온 상태라면 변환 작업을 바로 실행한다.
   */
  const isRunGenerateVideo = useRef(false)

  useEffect(() => {
    const run = async () => {
      if (isRunGenerateVideo.current) {
        return
      }

      const trimmedTopicFromProps = topicFromProps.trim()

      if (!trimmedTopicFromProps) {
        return
      }

      /**
       * 사용량 차감이 가능한지 검사
       */
      if (!checkCanCharge()) {
        return
      }

      try {
        isRunGenerateVideo.current = true
        setIsShownFullLoadingProgress(true)

        const generated = await generateVideo()

        if (!generated) {
          throw new Error('Failed to generate video')
        }

        /**
         * 사용량 차감\
         * 무료 사용 횟수 또는 크레딧 차감
         */
        await chargeUsage(generated.video.duration)

        await new Promise((r) => setTimeout(r, 1000))
      } catch (e) {
        // do nothing...
      } finally {
        setIsShownFullLoadingProgress(false)
      }
    }

    run()
  }, [generateVideo, topicFromProps, checkCanCharge, chargeUsage])

  /**
   * 입력란을 에러 상태로 표시 여부
   */
  const isShowingError = useMemo(() => {
    /**
     * 생성 또는 변환 API에서 에러가 발생했고\
     * API 호출 이후 파일이 변경되지 않았다면\
     * 오류로 표시
     */
    return (!!errorGenerateVideo || !!errorCreateJob) && !isChangedAfterError
  }, [errorGenerateVideo, errorCreateJob, isChangedAfterError])

  /**
   * API에서 발생한 에러 메시지 꺼내기
   */
  const errorMessageGenerateVideo = useMemo(() => {
    if (!errorGenerateVideo) {
      return ''
    }

    if (!('data' in errorGenerateVideo)) {
      return ''
    }

    const data = errorGenerateVideo.data as {
      error?: {
        code: string
        msg: string
      }
    }

    return data.error?.msg || ''
  }, [errorGenerateVideo])

  const errorMessageCreateJob = useMemo(() => {
    if (!errorCreateJob) {
      return ''
    }

    if (!('data' in errorCreateJob)) {
      return ''
    }

    const data = errorCreateJob.data as {
      error?: {
        code: string
        msg: string
      }
    }

    return data.error?.msg || ''
  }, [errorCreateJob])

  const errorMessage = errorMessageGenerateVideo || errorMessageCreateJob

  /**
   * 에러 메시지는 우선 콘솔에만 표시한다.
   */
  useEffect(() => {
    if (!errorMessage) {
      return
    }

    console.error(errorMessage)
  }, [errorMessage])

  /**
   * 현재 단계
   */
  const currentStep = useMemo(() => {
    if (isGeneratingVideo) {
      return 1
    } else if (!!generatedVideo) {
      return 2
    }

    return 0
  }, [isGeneratingVideo, generatedVideo])

  return (
    <ToolsLayout
      step={{
        currentStep,
        steps: [
          {
            title: t('Enter a topic'),
          },
          {
            title: t('Generating'),
          },
          {
            title: t('Get videos'),
          },
        ],
      }}
    >
      <GeneratorContainer>
        <TitleAndDescription>
          <h1>
            <span className="underline">{t('AI Art')}</span>{' '}
            <span>{t('Generator')}</span>
          </h1>
          <div>
            <p>
              {t(
                'Imagine, describe, & generate to watch your words transform into the exact images or videos you need to level up your videos.',
              )}
            </p>
          </div>
        </TitleAndDescription>

        <ContentContainer>
          {isShownFullLoadingProgress ? (
            <FullLoadingProgress isCompleted={!!generatedVideo} />
          ) : (
            <GeneratorForm
              topic={topic}
              onChangeTopic={setTopic}
              disabledChangeTopic={disabledChangeTopic}
              onGenerate={handleGenerate}
              disabledGenerate={disabledGenerate}
              videoSrc={generatedVideo?.video.videoUrl}
              isGenerating={isGeneratingVideo}
              isGenerated={isGeneratedVideo}
              isError={isShowingError}
              onDownload={handleDownload}
              onCreateVideo={handleCreateVideo}
              disabledExport={disabledExport}
              isLoadingUserInfo={isLoadingUserInfo}
              isLoggedIn={isLoggedIn}
              isPaidUser={isPaidUser}
              remainingFreeTrialCount={remainingFreeTrialCount}
            />
          )}
        </ContentContainer>

        <LicenseAndTerms>
          <Trans
            i18nKey={
              'By generating an image & video, you are agreeing to the DeepBrain AI, Inc. <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>.'
            }
            components={{
              terms: <a href={TERMS_OF_SERVICE_URL} target="_blank" />,
              privacy: <a href={PRIVACY_POLICY_URL} target="_blank" />,
            }}
          />
        </LicenseAndTerms>
      </GeneratorContainer>

      <StripeModal />

      {/* 
        무료 체험 횟수를 모두 사용한 경우 모달
      */}
      <CannotChargeFreeTrialModal
        isOpened={isOpenedCannotChargeFreeTrialModal}
        onClose={handleCloseCannotChargeFreeTrialModal}
      />

      {contextHolder}
    </ToolsLayout>
  )
}

const GeneratorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  align-items: center;

  height: 100%;
  /* 쿠키 관련 안내가 하단에서 80px임 */
  padding: 24px 24px ${80 + 24}px;

  overflow: auto;

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    padding: 10px;
  }
`

const TitleAndDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;

  h1 {
    font-size: 50px;
    line-height: 58px;
    font-weight: 700;
    color: #000000;
    text-align: center;

    margin: 0;

    span.underline {
      color: #146ef5;
      letter-spacing: -0.5px;
    }
  }

  div {
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: -0.5px;

    max-width: 600px;

    p {
      margin: 0;
    }
  }

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    h1 {
      font-size: 36px;
      line-height: 50.4px;
    }
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  box-sizing: border-box;
  width: 100%;
  max-width: 900px;
  min-height: 520px;

  border: 2px solid #146ef5;
  border-radius: 20px;
  box-shadow: 0px 0px 20px 0px #146ef51a;
`

const LicenseAndTerms = styled.div`
  font-size: 13px;
  line-height: 18.2px;
  letter-spacing: -2.5%;
  text-align: center;
  color: #989898;

  a {
    color: #146ef5;
  }
`
